import React, { createContext, useContext, useMemo, useState } from 'react';
import { Eye, EyeSlash } from '@phosphor-icons/react';

const LineRevealContext = createContext({ revealed: false, toggle: () => {} });

export const money = (n, currency = 'INR') => {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `₹${Math.round(v).toLocaleString('en-IN')}`;
  }
};

export function LineRevealProvider({ children }) {
  const [revealed, setRevealed] = useState(false);
  const value = useMemo(
    () => ({ revealed, toggle: () => setRevealed((v) => !v) }),
    [revealed],
  );
  return <LineRevealContext.Provider value={value}>{children}</LineRevealContext.Provider>;
}

export function useLineReveal() {
  return useContext(LineRevealContext);
}

/** Line-item amounts: hidden as *** until super admin clicks View details. */
export function LineMoney({ value, currency = 'INR', empty = '—' }) {
  const { revealed } = useLineReveal();
  if (value == null || value === '') return empty;
  if (!revealed) {
    return (
      <span style={{ letterSpacing: '0.18em', fontFamily: 'IBM Plex Mono, monospace', color: '#9CA3AF' }}>
        ***
      </span>
    );
  }
  return <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{money(value, currency)}</span>;
}

export function LineBilling({ amount, period, currency = 'INR', amcAmount }) {
  const { revealed } = useLineReveal();
  const hasPo = amount != null && amount !== '';
  const hasAmc = amcAmount != null && amcAmount !== '';
  if (!hasPo && !hasAmc) return '—';
  const poLabel = hasAmc || !period || ['one_time', 'onetime', 'once', ''].includes(String(period))
    ? 'one-time'
    : String(period).replace('_', ' ');
  if (!revealed) {
    return (
      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
        <span style={{ letterSpacing: '0.18em', fontFamily: 'IBM Plex Mono, monospace' }}>***</span>
        {hasPo ? ` ${poLabel}` : ''}
        {hasAmc ? ' · AMC/yr' : ''}
      </span>
    );
  }
  const parts = [];
  if (hasPo) parts.push(`PO ${money(amount, currency)} ${poLabel}`);
  if (hasAmc) parts.push(`AMC ${money(amcAmount, currency)}/yr`);
  return parts.join(' · ');
}

export function StatCard({ label, value, sub, color = '#002FA7' }) {
  return (
    <div style={{
      background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px',
      padding: '12px 14px', borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: '11px', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {label}
      </div>
      <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginTop: '4px', fontFamily: 'IBM Plex Mono, monospace' }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{sub}</div> : null}
    </div>
  );
}

export function Panel({ title, children, actions, style }) {
  return (
    <div style={{
      background: 'white', border: '1px solid #E5E7EB', borderRadius: '10px',
      padding: '14px', marginBottom: '14px', ...style,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: title ? '10px' : 0 }}>
        {title ? <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 700 }}>{title}</h3> : <span />}
        {actions}
      </div>
      {children}
    </div>
  );
}

export function MarginCell({ value, pct, alwaysShow }) {
  const { revealed } = useLineReveal();
  if (!alwaysShow && !revealed) {
    return (
      <span style={{ letterSpacing: '0.18em', fontFamily: 'IBM Plex Mono, monospace', color: '#9CA3AF' }}>
        ***
      </span>
    );
  }
  const v = Number(value) || 0;
  const color = v >= 0 ? '#059669' : '#DC2626';
  return (
    <span style={{ color, fontWeight: 600, fontFamily: 'IBM Plex Mono, monospace' }}>
      {money(v)}
      {pct != null ? <span style={{ color: '#9CA3AF', fontWeight: 500 }}> ({pct}%)</span> : null}
    </span>
  );
}

export function EmptyHint({ children }) {
  return <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>{children}</p>;
}

export function EconomicsSourceNote() {
  const { revealed, toggle } = useLineReveal();
  return (
    <div style={{
      display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'space-between',
      margin: '8px 0 12px', flexWrap: 'wrap',
    }}>
      <p style={{ fontSize: 12, color: '#64748B', margin: 0, flex: 1, minWidth: '220px' }}>
        Totals stay visible. Line items (people CTC, each asset, each client bill) start hidden —
        click View details to reveal. Monthly revenue is yearly AMC ÷ 12 from Accounts; PO is one-time.
        CTC from Payroll, asset cost from IT.
      </p>
      <button
        type="button"
        className="btn-secondary"
        onClick={toggle}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, flexShrink: 0 }}
      >
        {revealed ? <EyeSlash size={15} /> : <Eye size={15} />}
        {revealed ? 'Hide details' : 'View details'}
      </button>
    </div>
  );
}

export function SuperAdminGate({ user, children }) {
  if (user?.role !== 'super_admin') {
    return (
      <div style={{
        background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px',
        padding: '20px', color: '#991B1B', fontSize: '13px',
      }}>
        Portfolio economics is available to super admins only.
      </div>
    );
  }
  return <LineRevealProvider>{children}</LineRevealProvider>;
}
